<script setup lang="ts">
  import { computed } from "vue";
  import { Input } from "@/components/ui/input";
  import { Select, type SelectOption } from "@/components/episto-ui/select";
  import { Switch } from "@/components/ui/switch";
  import { Tooltip } from "@/components/ui/tooltip";
  import { FormDialog, FormField, ImageUploadField } from "@/components/episto-ui/form-dialog";
  import type { Account } from "@/types";

  export interface ChatPageForm {
    id?: number;
    external_id?: string;
    name?: string;
    image_url?: string | null;
    account_ids?: number[];
    default_account_ids?: number[];
    created_at?: string | null;
  }

  const chatPage = defineModel<ChatPageForm>("chatPage", { required: true });

  const props = defineProps<{
    open: boolean;
    isEditing?: boolean;
    availableAccounts: SelectOption[];
    accounts: Account[];
    showExternalId?: boolean;
    accountsLocked?: boolean;
  }>();

  const emit = defineEmits<{
    "update:open": [value: boolean];
    save: [];
  }>();

  const imageUrl = computed({
    get: () => chatPage.value.image_url ?? "",
    set: (newValue: string) => {
      chatPage.value.image_url = newValue;
    },
  });

  const accountIds = computed({
    get: () => chatPage.value.account_ids ?? [],
    set: (newValue: (string | number)[]) => {
      chatPage.value.account_ids = newValue.map(Number);
    },
  });

  const selectedAccounts = computed<Account[]>(() =>
    accountIds.value
      .map((id) => props.accounts.find((account) => account.id === id))
      .filter((account): account is Account => Boolean(account)),
  );

  // An account already served by another default page locks the toggle: one default per account
  const lockingChatPage = (account: Account) => {
    const current = account.default_chat_page;
    if (!current || current.id === chatPage.value.id) return null;
    return current;
  };

  const isDefaultAccount = (accountId: number) => (chatPage.value.default_account_ids ?? []).includes(accountId);

  const toggleDefaultAccount = (accountId: number, isDefault: boolean) => {
    const current = chatPage.value.default_account_ids ?? [];
    chatPage.value.default_account_ids = isDefault
      ? [...current, accountId]
      : current.filter((id) => id !== accountId);
  };

  const canSave = computed(() => (chatPage.value.name ?? "").trim().length > 0);
</script>

<template>
  <FormDialog
    :open="open"
    :is-editing="isEditing"
    :can-save="canSave"
    entity-name="Chat page"
    max-width="600px"
    @update:open="emit('update:open', $event)"
    @save="emit('save')">
    <FormField
      v-if="showExternalId"
      id="external_id"
      label="External ID">
      <Input
        id="external_id"
        v-model="chatPage.external_id"
        placeholder="e.g., my-page-id"
        class="text-sm font-mono" />
    </FormField>

    <FormField
      id="name"
      label="Name">
      <Input
        id="name"
        v-model="chatPage.name"
        placeholder="Page name"
        class="text-sm" />
      <p class="text-xs text-muted-foreground">
        Shown as the browser tab title when a respondent opens the questionnaire.
      </p>
    </FormField>

    <ImageUploadField
      v-model="imageUrl"
      label="Logo" />

    <FormField
      id="accounts"
      label="Accounts">
      <Select
        :model-value="accountIds"
        :options="availableAccounts"
        :disabled="accountsLocked"
        placeholder="Select at least one account"
        multiple
        filterable
        @update:model-value="accountIds = $event as number[]" />

      <div
        v-if="selectedAccounts.length"
        class="mt-3 space-y-2">
        <div
          v-for="account in selectedAccounts"
          :key="account.id"
          class="flex items-center justify-between gap-3">
          <span class="text-sm">{{ account.name }}</span>
          <div class="flex items-center gap-2">
            <span class="text-xs text-muted-foreground">Default page</span>
            <Tooltip
              :disabled="!lockingChatPage(account)"
              :content="`${account.name} already uses ${lockingChatPage(account)?.name} as default page. Unset it first.`">
              <span class="inline-flex">
                <Switch
                  :model-value="isDefaultAccount(account.id)"
                  :disabled="Boolean(lockingChatPage(account))"
                  @update:model-value="toggleDefaultAccount(account.id, $event)" />
              </span>
            </Tooltip>
          </div>
        </div>
        <p class="text-xs text-muted-foreground">
          Questionnaire links without an explicit page use the account's default page.
        </p>
      </div>
    </FormField>
  </FormDialog>
</template>
